import { CaptureVideoOption } from "@zoom/videosdk";
import { ZoomMediaStream } from "./index-types";
import { START_AUDIO_OPTIONS, START_VIDEO_OPTIONS } from "./zoom-constant";
import {
  PreviewMediaPreferences,
  SessionMediaHandoffResult,
} from "./zoom-types";

export const getStartVideoOptions = (
  mediaStream: ZoomMediaStream | null,
  cameraId?: string,
  virtualBackgroundImageUrl?: string
): CaptureVideoOption => {
  const options: CaptureVideoOption = {
    ...START_VIDEO_OPTIONS,
    ptz: mediaStream?.isBrowserSupportPTZ(),
  };

  if (cameraId) {
    options.cameraId = cameraId;
  }

  if (virtualBackgroundImageUrl && mediaStream?.isSupportVirtualBackground()) {
    options.virtualBackground = { imageUrl: virtualBackgroundImageUrl };
  }

  return options;
};

/**
 * Applies the preview screen's camera/microphone choices to the joined session.
 *
 * The preview captures the devices with standalone local tracks, which are not
 * the session's tracks — without this handoff the call always started with the
 * camera off and the microphone muted, whatever the user picked in the preview.
 * The caller must release the preview tracks first: a camera held by the local
 * track is still busy, and `startVideo` fails on it.
 *
 * Audio and video are started independently so one failing device does not take
 * the other down with it.
 */
export const applyPreviewMediaToSession = async (
  mediaStream: ZoomMediaStream,
  preferences: PreviewMediaPreferences
): Promise<SessionMediaHandoffResult> => {
  const { isVideoOn, isAudioMuted, cameraId, microphoneId, speakerId } =
    preferences;
  const result: SessionMediaHandoffResult = {
    isAudioStarted: false,
    isVideoStarted: false,
  };

  await mediaStream.startAudio({
    ...START_AUDIO_OPTIONS,
    mute: isAudioMuted,
    ...(microphoneId ? { microphoneId } : {}),
    ...(speakerId ? { speakerId } : {}),
  });
  /* `mute` is not honoured on every browser, so assert the preview state. */
  if (isAudioMuted !== mediaStream.isAudioMuted()) {
    await (isAudioMuted ? mediaStream.muteAudio() : mediaStream.unmuteAudio());
  }
  result.isAudioStarted = true;

  if (isVideoOn) {
    try {
      await mediaStream.startVideo(getStartVideoOptions(mediaStream, cameraId));
      result.isVideoStarted = true;
    } catch (error) {
      /* The preview's camera id can be stale or blank — the session default is
         better than joining with the camera off after asking for it on. */
      console.error("Failed to start video with preview camera:", error);
      if (cameraId) {
        try {
          await mediaStream.startVideo(getStartVideoOptions(mediaStream));
          result.isVideoStarted = true;
        } catch (fallbackError) {
          console.error(
            "Failed to start video with default camera:",
            fallbackError
          );
        }
      }
    }
  }

  return result;
};
